/**
 * 错误处理工具
 * 统一处理前端错误和控制台问题
 */

import { devError, devWarn, devLog } from './typescript-helpers'

// 错误类型定义
export interface ErrorInfo {
  message: string
  stack?: string
  componentStack?: string
  errorBoundary?: string
  errorBoundaryStack?: string
  source?: 'vue' | 'javascript' | 'network' | 'promise' | 'resource'
  timestamp: number
  url: string
  userAgent: string
}

// 错误处理器类
export class ErrorHandler {
  private static instance: ErrorHandler
  private errorQueue: ErrorInfo[] = []
  private maxQueueSize = 50

  private constructor() {
    this.setupGlobalErrorHandlers()
  }

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler()
    }
    return ErrorHandler.instance
  }

  /**
   * 设置全局错误处理器
   */
  private setupGlobalErrorHandlers(): void {
    // JavaScript 运行时错误
    window.addEventListener('error', (event) => {
      this.handleError({
        message: event.message,
        stack: event.error?.stack,
        source: 'javascript',
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      })
    })

    // Promise 未捕获的拒绝
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        source: 'promise',
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      })
    })

    // 资源加载错误
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement & { src?: string; href?: string }
        const resourceUrl = target?.src || target?.href

        // 过滤掉内联SVG和data URL的错误，这些通常是无害的
        if (resourceUrl && (
          resourceUrl.startsWith('data:image/svg+xml') ||
          resourceUrl.includes('svg') ||
          target.tagName === 'svg' ||
          target.tagName === 'SVG'
        )) {
          return // 忽略SVG相关的错误
        }

        this.handleError({
          message: `Resource loading error: ${resourceUrl}`,
          source: 'resource',
          timestamp: Date.now(),
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      }
    }, true)
  }

  /**
   * 处理错误
   */
  public handleError(errorInfo: Partial<ErrorInfo>): void {
    const fullErrorInfo: ErrorInfo = {
      message: errorInfo.message || 'Unknown error',
      stack: errorInfo.stack,
      componentStack: errorInfo.componentStack,
      errorBoundary: errorInfo.errorBoundary,
      errorBoundaryStack: errorInfo.errorBoundaryStack,
      source: errorInfo.source || 'javascript',
      timestamp: errorInfo.timestamp || Date.now(),
      url: errorInfo.url || window.location.href,
      userAgent: errorInfo.userAgent || navigator.userAgent
    }

    // 添加到错误队列
    this.addToQueue(fullErrorInfo)

    // 开发环境下输出到控制台
    if (import.meta.env.DEV) {
      this.logError(fullErrorInfo)
    }

    // 生产环境下可以发送到错误监控服务
    if (import.meta.env.PROD) {
      this.reportError(fullErrorInfo)
    }
  }

  /**
   * 添加错误到队列
   */
  private addToQueue(errorInfo: ErrorInfo): void {
    this.errorQueue.push(errorInfo)

    // 保持队列大小
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift()
    }
  }

  /**
   * 开发环境下记录错误
   */
  private logError(errorInfo: ErrorInfo): void {
    const { message, stack, source, componentStack } = errorInfo

    devError(`[${source?.toUpperCase() || 'UNKNOWN'}] ${message}`, {
      stack,
      componentStack,
      timestamp: new Date(errorInfo.timestamp).toISOString(),
      url: errorInfo.url
    })
  }

  /**
   * 生产环境下报告错误
   */
  private reportError(errorInfo: ErrorInfo): void {
    // 这里可以集成 Sentry、LogRocket 等错误监控服务
    try {
      if (window.Sentry) {
        window.Sentry.captureException(new Error(errorInfo.message))
      }
    } catch (error) {
      devWarn('Failed to report error to monitoring service', error)
    }
  }

  /**
   * 获取错误队列
   */
  public getErrorQueue(): ErrorInfo[] {
    return [...this.errorQueue]
  }

  /**
   * 清空错误队列
   */
  public clearErrorQueue(): void {
    this.errorQueue = []
  }

  /**
   * Vue 错误处理器
   */
  public vueErrorHandler(error: Error, _instance: unknown, info: string): void {
    this.handleError({
      message: error.message,
      stack: error.stack,
      componentStack: info,
      source: 'vue',
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent
    })
  }
}

// 控制台增强工具
export class ConsoleEnhancer {
  private static originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug
  }

  /**
   * 增强控制台输出
   */
  public static enhance(): void {
    if (import.meta.env.DEV) {
      // 开发环境下增强控制台
      console.log = (...args: any[]) => {
        ConsoleEnhancer.originalConsole.log(
          `%c[${new Date().toLocaleTimeString()}]`,
          'color: #2196F3; font-weight: bold;',
          ...args
        )
      }

      console.warn = (...args: any[]) => {
        ConsoleEnhancer.originalConsole.warn(
          `%c[${new Date().toLocaleTimeString()}]`,
          'color: #FF9800; font-weight: bold;',
          ...args
        )
      }

      console.error = (...args: any[]) => {
        ConsoleEnhancer.originalConsole.error(
          `%c[${new Date().toLocaleTimeString()}]`,
          'color: #F44336; font-weight: bold;',
          ...args
        )
      }
    }
  }

  /**
   * 恢复原始控制台
   */
  public static restore(): void {
    Object.assign(console, ConsoleEnhancer.originalConsole)
  }
}

// 性能监控工具
export class PerformanceMonitor {
  private static marks: Map<string, number> = new Map()

  /**
   * 开始性能标记
   */
  public static mark(name: string): void {
    if (import.meta.env.DEV) {
      PerformanceMonitor.marks.set(name, performance.now())
      devLog(`Performance mark: ${name}`)
    }
  }

  /**
   * 结束性能标记并输出耗时
   */
  public static measure(name: string): number {
    if (import.meta.env.DEV) {
      const startTime = PerformanceMonitor.marks.get(name)
      if (startTime) {
        const duration = performance.now() - startTime
        devLog(`Performance measure: ${name} took ${duration.toFixed(2)}ms`)
        PerformanceMonitor.marks.delete(name)
        return duration
      }
    }
    return 0
  }

  /**
   * 监控组件渲染性能
   */
  public static monitorComponent(componentName: string): {
    start: () => void
    end: () => void
  } {
    return {
      start: () => PerformanceMonitor.mark(`component-${componentName}-start`),
      end: () => PerformanceMonitor.measure(`component-${componentName}-start`)
    }
  }
}

// 导出单例实例
export const errorHandler = ErrorHandler.getInstance()

// 默认导出
export default {
  ErrorHandler,
  ConsoleEnhancer,
  PerformanceMonitor,
  errorHandler
}